import { useState, useEffect } from 'react'
import { FiDownload, FiShield, FiSearch, FiChevronDown, FiChevronUp } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { PageHeader, Btn } from '../ui'
import { formatearFechaHoraSeg } from '../utils/fechas'

const ReporteAuditoria = () => {
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    tabla: 'todos',
    accion: 'todos',
    usuarioId: 'todos'
  })
  const [auditorias, setAuditorias] = useState([])
  const [empleados, setEmpleados] = useState([])
  const [loading, setLoading] = useState(false)
  const [generandoPDF, setGenerandoPDF] = useState(false)
  const [resumen, setResumen] = useState(null)
  const [expandedRow, setExpandedRow] = useState(null)

  // Helper para construir URL con empresa_id si estamos en modo gestión
  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaGestionId) {
      const separator = endpoint.includes('?') ? '&' : '?'
      return `${API_URL}${endpoint}${separator}empresa_id=${empresaGestionId}`
    }
    return `${API_URL}${endpoint}`
  }

  useEffect(() => {
    cargarEmpleados()
  }, [empresaGestionId])

  const cargarEmpleados = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/empleados'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setEmpleados(data.empleados || [])
      }
    } catch (error) {
      console.error('Error al cargar empleados:', error)
    }
  }

  const cargarAuditorias = async () => {
    // Validar fechas
    if (!filtros.fechaInicio || !filtros.fechaFin) {
      toast.error('Debe seleccionar fecha de inicio y fin')
      return
    }

    // Validar que fechaFin >= fechaInicio
    if (new Date(filtros.fechaFin) < new Date(filtros.fechaInicio)) {
      toast.error('La fecha fin no puede ser anterior a la fecha de inicio')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token')

      // Construir query params
      const params = new URLSearchParams({
        fechaInicio: filtros.fechaInicio,
        fechaFin: filtros.fechaFin
      })

      if (filtros.tabla !== 'todos') {
        params.append('tabla', filtros.tabla)
      }

      if (filtros.accion !== 'todos') {
        params.append('accion', filtros.accion)
      }

      if (filtros.usuarioId !== 'todos') {
        params.append('usuario_id', filtros.usuarioId)
      }

      if (esGestion && empresaGestionId) {
        params.append('empresa_id', empresaGestionId)
      }

      const response = await fetch(`${API_URL}/auditoria/fichajes?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAuditorias(data.registros)
        setResumen(data.resumen)

        if (data.registros.length === 0) {
          toast('No se encontraron registros de auditoría para los filtros seleccionados', { icon: 'ℹ️' })
        } else {
          toast.success(`Se encontraron ${data.registros.length} registros de auditoría`)
        }
      } else if (response.status === 403) {
        toast.error('Acceso denegado. Solo administradores pueden acceder a este reporte.')
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al cargar auditorías')
      }
    } catch (error) {
      console.error('Error al cargar auditorías:', error)
      toast.error('Error al cargar auditorías. Verifica tu conexión.')
    } finally {
      setLoading(false)
    }
  }

  const exportarPDF = async () => {
    if (auditorias.length === 0) {
      toast.error('No hay datos para exportar')
      return
    }

    setGenerandoPDF(true)
    try {
      const token = localStorage.getItem('token')

      // Construir query params
      const params = new URLSearchParams({
        fechaInicio: filtros.fechaInicio,
        fechaFin: filtros.fechaFin
      })

      if (filtros.tabla !== 'todos') {
        params.append('tabla', filtros.tabla)
      }

      if (filtros.accion !== 'todos') {
        params.append('accion', filtros.accion)
      }

      if (filtros.usuarioId !== 'todos') {
        params.append('usuario_id', filtros.usuarioId)
      }

      const response = await fetch(`${API_URL}/auditoria/fichajes/exportar-pdf?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `auditoria_fichajes_${filtros.fechaInicio}_${filtros.fechaFin}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        toast.success('Reporte PDF generado correctamente')
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al generar PDF')
      }
    } catch (error) {
      console.error('Error generando PDF:', error)
      toast.error('Error al generar PDF. Intenta con un rango más pequeño.')
    } finally {
      setGenerandoPDF(false)
    }
  }

  const formatearCambios = (datosAnteriores, datosNuevos, accion) => {
    if (accion === 'INSERT') {
      return <span className="badge badge-success">Nuevo registro</span>
    }

    if (accion === 'DELETE') {
      return <span className="badge badge-danger">Registro eliminado</span>
    }

    if (accion === 'UPDATE' && datosAnteriores && datosNuevos) {
      try {
        const antes = JSON.parse(datosAnteriores)
        const despues = JSON.parse(datosNuevos)

        const cambios = []
        for (const campo in despues) {
          // Ignorar timestamps para comparación
          if (campo === 'updated_at' || campo === 'created_at') continue

          if (antes[campo] !== despues[campo]) {
            cambios.push(
              <div key={campo} className="text-xs mb-1">
                <span className="font-semibold">{campo}:</span>{' '}
                <span className="text-red-600">{antes[campo] === null || antes[campo] === undefined ? 'null' : String(antes[campo])}</span>
                {' → '}
                <span className="text-green-600">{despues[campo] === null || despues[campo] === undefined ? 'null' : String(despues[campo])}</span>
              </div>
            )
          }
        }

        return cambios.length > 0 ? (
          <div>{cambios}</div>
        ) : (
          <span className="text-gray-400 text-xs italic">Sin cambios detectados</span>
        )
      } catch (e) {
        return <span className="text-gray-400 text-xs italic">Error al parsear cambios</span>
      }
    }

    return null
  }

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ A1"
        kicker="Auditoría"
        title="Registros de"
        italic="cambios."
        sub="Consulta y exporta registros de cambios en el sistema de fichajes."
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Panel de filtros */}
      <div className="card-glass mb-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 whitespace-nowrap">Desde</label>
            <input
              type="date"
              value={filtros.fechaInicio}
              onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
              className="input-glass text-sm"
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 whitespace-nowrap">Hasta</label>
            <input
              type="date"
              value={filtros.fechaFin}
              onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
              className="input-glass text-sm"
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 whitespace-nowrap">Tabla</label>
            <select
              value={filtros.tabla}
              onChange={(e) => setFiltros({ ...filtros, tabla: e.target.value })}
              className="input-glass text-sm"
            >
              <option value="todos">Todas</option>
              <option value="fichajes">Fichajes</option>
              <option value="pausas_fichaje">Pausas</option>
              <option value="correcciones_fichajes">Correcciones</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 whitespace-nowrap">Acción</label>
            <select
              value={filtros.accion}
              onChange={(e) => setFiltros({ ...filtros, accion: e.target.value })}
              className="input-glass text-sm"
            >
              <option value="todos">Todas</option>
              <option value="INSERT">INSERT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 whitespace-nowrap">Usuario</label>
            <select
              value={filtros.usuarioId}
              onChange={(e) => setFiltros({ ...filtros, usuarioId: e.target.value })}
              className="input-glass text-sm"
            >
              <option value="todos">Todos</option>
              {empleados.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={cargarAuditorias}
            disabled={loading || !filtros.fechaInicio || !filtros.fechaFin}
            className="btn btn-primary btn-ripple btn-sm flex items-center gap-2"
          >
            <FiSearch />
            {loading ? 'Cargando...' : 'Buscar'}
          </button>

          <button
            onClick={exportarPDF}
            disabled={generandoPDF || auditorias.length === 0}
            className="btn btn-secondary btn-sm flex items-center gap-2"
          >
            <FiDownload />
            {generandoPDF ? 'Generando...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Tabla de auditorías */}
      {auditorias.length > 0 && (
        <div className="card-glass overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Registros de Auditoría
              {auditorias.length >= 100 && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  (Mostrando primeros 100 de {resumen?.total || auditorias.length})
                </span>
              )}
            </h2>
            {auditorias.length >= 1000 && (
              <span className="badge badge-warning">
                Límite de 1000 registros alcanzado
              </span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha/Hora
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tabla
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acción
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reg. ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cambios
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">

                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditorias.slice(0, 100).map((auditoria) => (
                  <>
                    <tr
                      key={auditoria.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleRow(auditoria.id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {formatearFechaHoraSeg(auditoria.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {auditoria.empleado_nombre || 'Sistema'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {auditoria.tabla}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={
                          auditoria.accion === 'INSERT' ? 'badge badge-success' :
                          auditoria.accion === 'UPDATE' ? 'badge badge-warning' :
                          'badge badge-danger'
                        }>
                          {auditoria.accion}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {auditoria.registro_id}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatearCambios(auditoria.datos_anteriores, auditoria.datos_nuevos, auditoria.accion)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {expandedRow === auditoria.id ? <FiChevronUp /> : <FiChevronDown />}
                      </td>
                    </tr>

                    {/* Fila expandida con JSON completo */}
                    {expandedRow === auditoria.id && (
                      <tr>
                        <td colSpan="7" className="px-4 py-4 bg-gradient-to-r from-gray-50 to-gray-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-semibold text-sm mb-2 text-gray-700">Datos Anteriores</h4>
                              <pre className="bg-white p-3 rounded-lg text-xs overflow-auto max-h-60 border border-gray-200">
                                {auditoria.datos_anteriores ? JSON.stringify(JSON.parse(auditoria.datos_anteriores), null, 2) : 'N/A'}
                              </pre>
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm mb-2 text-gray-700">Datos Nuevos</h4>
                              <pre className="bg-white p-3 rounded-lg text-xs overflow-auto max-h-60 border border-gray-200">
                                {auditoria.datos_nuevos ? JSON.stringify(JSON.parse(auditoria.datos_nuevos), null, 2) : 'N/A'}
                              </pre>
                            </div>
                          </div>
                          {auditoria.ip && (
                            <div className="mt-3 text-xs text-gray-600">
                              <strong>IP:</strong> {auditoria.ip}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {auditorias.length > 100 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
              ⚠️ Mostrando los primeros 100 de {auditorias.length} registros. Usa la exportación PDF para ver todos los registros.
            </div>
          )}
        </div>
      )}

      {/* Mensaje cuando no hay datos */}
      {auditorias.length === 0 && !loading && (
        <div className="card-glass text-center py-12">
          <FiShield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-3 text-sm font-medium text-gray-900">
            No hay datos para mostrar
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Selecciona un rango de fechas y haz clic en "Generar Vista Previa"
          </p>
        </div>
      )}
    </div>
  )
}

export default ReporteAuditoria
